import {
  AutocompleteMultiselection,
  FilterQuery,
  YesObject
} from '@yes.technology/react-toolkit'
import {
  ComponentProps,
  useCallback,
  useImperativeHandle,
  useMemo
} from 'react'

import {
  concatenateField,
  extractFieldsFromString
} from 'shared/utils/object/concatenateFields'
import useSuggestionsOnChange from '../../hooks/useSuggestionsOnChange'
import { FieldValues } from '../../types'
import { ParentRefProps } from './Field'

type AutocompleteMultiselectionProps = ComponentProps<
  typeof AutocompleteMultiselection
>

type Option = ComponentProps<typeof AutocompleteMultiselection>['options'][0]

type GetOptionsParams = {
  selectedOptions: Option[] | undefined
  suggestions: YesObject[]
  fieldConcatenation?: string
}

const getOptions = ({
  selectedOptions,
  suggestions,
  fieldConcatenation
}: GetOptionsParams) => {
  const mappedSuggestions = suggestions.map((suggestion) => {
    let desConcatenation: string | undefined
    if (fieldConcatenation && suggestion) {
      desConcatenation = concatenateField(suggestion, fieldConcatenation, true)
    }

    return {
      id: suggestion.uuid,
      value: desConcatenation || suggestion.des
    }
  })

  if (!selectedOptions) {
    return []
  }

  const uniqueSelectedOptions = selectedOptions.filter(
    (selectedOption) =>
      !suggestions.find((suggestion) => suggestion.uuid === selectedOption.id)
  )

  const options: Option[] = [...uniqueSelectedOptions, ...mappedSuggestions]

  return options
}

type AutocompleteMultiselectionFieldProps = {
  inputRef?: React.Ref<HTMLInputElement>
  parentRef?: React.Ref<ParentRefProps>
  objectclassUuid: string
  onChangeValues: (values: FieldValues['']['value']) => void
  interdependentFilteringQuery?: FilterQuery
  fieldConcatenation?: string
} & Partial<AutocompleteMultiselectionProps>

const AutocompleteMultiselectionField = ({
  inputRef,
  parentRef,
  objectclassUuid,
  onChangeValues,
  selectedOptions = [],
  interdependentFilteringQuery,
  fieldConcatenation,
  statusClass = 'default',
  ...props
}: AutocompleteMultiselectionFieldProps) => {
  const fields = useMemo(
    () =>
      fieldConcatenation
        ? extractFieldsFromString(fieldConcatenation)
        : ['des'],
    [fieldConcatenation]
  )

  const projectFields = useMemo(
    () => Object.fromEntries(fields.map((field) => [field, 1])),
    [fields]
  )

  const { value, onChange, isLoading, suggestions, resetValue } =
    useSuggestionsOnChange({
      objectclassUuid,
      interdependentFilteringQuery,
      skipMetadata: false,
      skipRelations: false, // TODO: API Ignore projectFields with relations when skipRelations
      projectFields
    })

  useImperativeHandle(
    parentRef,
    () => ({
      onChangeInputValue: (value: string) => {
        onChange(value)
      }
    }),
    [onChange]
  )

  const onSelect = useCallback(
    (targetSelection: Option) => {
      const isOptionAlreadySelected = selectedOptions.find(
        ({ id }) => id === targetSelection.id
      )

      const optionsAfterSelection = [...selectedOptions, targetSelection]
      const optionsAfterDeselection = selectedOptions.filter(
        ({ id }) => id !== targetSelection.id
      )

      const newSelectedOptions = isOptionAlreadySelected
        ? optionsAfterDeselection
        : optionsAfterSelection

      if (newSelectedOptions.length === 0) {
        resetValue()
      }

      onChangeValues(
        newSelectedOptions.map((selectedOption) => ({
          uuid: selectedOption.id,
          des: selectedOption.value
        }))
      )
    },
    [onChangeValues, resetValue, selectedOptions]
  )

  const options = getOptions({
    selectedOptions,
    suggestions,
    fieldConcatenation
  })

  return (
    <AutocompleteMultiselection
      {...props}
      ref={inputRef}
      statusClass={isLoading ? 'processing' : statusClass}
      {...{ options, value, onChange, selectedOptions, onSelect }}
    />
  )
}

export default AutocompleteMultiselectionField
